import React, { useEffect, useMemo, useState } from "react";

const CATEGORIES = [
  "Equipment fault", "Stock shortage", "Staff issue",
  "Delivery complaint", "Hygiene / safety", "Other",
];
const PRIORITIES: Array<{ value: string; label: string }> = [
  { value: "low", label: "Low" }, { value: "medium", label: "Medium" },
  { value: "high", label: "High" }, { value: "urgent", label: "Urgent" },
];
const PRIORITY_NUM: Record<string, number> = { low: 1, medium: 2, high: 3, urgent: 4 };
const PRIO_LABEL: Record<number, string> = { 1: "Low", 2: "Medium", 3: "High", 4: "Urgent" };
const STATUS_LABEL: Record<number, string> = { 2: "Open", 3: "Pending", 4: "Resolved", 5: "Closed" };
const BRANCH_ID_TO_NAME: Record<string, string> = { GRD: "Gardens", PLM: "Plumstead", PRK: "Parklands", SEA: "Sea Point" };
const ALL_LOCATIONS = ["Gardens", "Plumstead", "Parklands", "Sea Point"];
const MAX_ATTACH_BYTES = 4_000_000;

type ListTicket = { id: number; subject: string; status: number; priority: number; tags?: string[]; createdAt: string; updatedAt: string };
type Convo = { id: number; body: string; fromEmail: string; incoming: boolean; private: boolean; createdAt: string };

function getUserIdentity(): { email: string; name: string; branch: string | null } {
  try {
    const raw = localStorage.getItem("gotrue.user");
    if (!raw) return { email: "", name: "", branch: null };
    const user = JSON.parse(raw);
    const email = user?.email || "";
    const name = user?.user_metadata?.full_name || user?.user_metadata?.name || "";
    let branch: string | null = null;
    const token: string | undefined = user?.token?.access_token;
    if (token) {
      const parts = token.split(".");
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]));
        const roles: unknown[] = payload?.app_metadata?.roles ?? [];
        if (Array.isArray(roles)) {
          for (const r of roles) {
            const m = String(r).match(/^branch([A-Za-z]{3})$/);
            if (m) { branch = BRANCH_ID_TO_NAME[m[1].toUpperCase()] || null; break; }
          }
        }
      }
    }
    return { email, name, branch };
  } catch { return { email: "", name: "", branch: null }; }
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const res = String(reader.result || "");
      const comma = res.indexOf(",");
      resolve(comma >= 0 ? res.slice(comma + 1) : res);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function fmt(iso: string): string {
  try { return new Date(iso).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function StatusPill({ status }: { status: number }) {
  const open = status === 2 || status === 3;
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 20,
      background: open ? "var(--ct-ok-bg)" : "var(--ct-surface-3, #eef1f5)",
      color: open ? "var(--ct-ok)" : "var(--ct-ink-3)", whiteSpace: "nowrap",
    }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export function FreshdeskTicketTab() {
  const identity = useMemo(getUserIdentity, []);
  const [mode, setMode] = useState<"new" | "list">("new");

  // ---- NEW TICKET form ----
  const [category, setCategory] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");
  const [manualEmail, setManualEmail] = useState("");
  const [branchPick, setBranchPick] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const effectiveEmail = (identity.email || manualEmail).trim();
  const effectiveBranch = identity.branch || branchPick;
  const canSubmit =
    !!category && subject.trim().length > 0 && description.trim().length > 0 &&
    effectiveEmail.length > 0 && !!effectiveBranch;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    if (file && file.size > MAX_ATTACH_BYTES) { setResult("Attachment too large — keep it under 4MB."); return; }
    setSubmitting(true); setResult(null);
    try {
      const body: any = {
        subject: subject.trim(),
        description:
          `Category: ${category}\nBranch: ${effectiveBranch}\nRaised by: ${identity.name || effectiveEmail}\n\n` +
          description.trim(),
        email: effectiveEmail,
        name: identity.name || undefined,
        priority: PRIORITY_NUM[priority] || 2,
        branch: effectiveBranch,
      };
      if (file) body.attachment = { name: file.name, type: file.type || "application/octet-stream", dataBase64: await fileToBase64(file) };

      const res = await fetch("/.netlify/functions/create-ticket", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) { setResult(`Could not create ticket: ${data.error || res.status}`); }
      else {
        setResult(`✓ Ticket #${data.id} created.`);
        setCategory(""); setSubject(""); setDescription(""); setPriority("medium"); setFile(null);
        if (!identity.branch) setBranchPick("");
      }
    } catch { setResult("Could not submit. Try again."); }
    finally { setSubmitting(false); }
  };

  // ---- TICKETS list + detail ----
  const [tickets, setTickets] = useState<ListTicket[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listErr, setListErr] = useState("");
  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<{ ticket: any; conversations: Convo[] } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);
  const [replyMsg, setReplyMsg] = useState<string | null>(null);

  const loadList = async () => {
    setListLoading(true); setListErr("");
    try {
      const res = await fetch("/.netlify/functions/list-tickets");
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.status);
      setTickets(data.tickets || []);
    } catch (e: any) { setListErr("Could not load tickets."); }
    finally { setListLoading(false); }
  };

  useEffect(() => { if (mode === "list" && openId === null) loadList(); }, [mode, openId]);

  // Branch isolation: a branch login sees only tickets tagged with its branch
  // (matched by name or slug, case-insensitive). An operator — no branch in the
  // token — sees every branch's tickets.
  const visibleTickets = useMemo(() => {
    if (!identity.branch) return tickets;
    const b = identity.branch.toLowerCase();
    const want = new Set([b, b.replace(/\s+/g, "-")]);
    return tickets.filter((t) => (t.tags || []).some((tag) => want.has(String(tag).toLowerCase())));
  }, [tickets, identity.branch]);

  const openTicket = async (id: number) => {
    setOpenId(id); setDetail(null); setDetailLoading(true); setReplyMsg(null); setReplyText("");
    try {
      const res = await fetch(`/.netlify/functions/get-ticket?id=${id}`);
      const data = await res.json();
      if (res.ok) setDetail(data);
    } catch { /* ignore */ }
    finally { setDetailLoading(false); }
  };

  const sendReply = async () => {
    if (!replyText.trim() || replying || openId === null) return;
    setReplying(true); setReplyMsg(null);
    try {
      const res = await fetch("/.netlify/functions/reply-ticket", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: openId, body: replyText.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) { setReplyMsg(`Could not send: ${data.error || res.status}`); }
      else { setReplyText(""); setReplyMsg("✓ Reply sent."); openTicket(openId); }
    } catch { setReplyMsg("Could not send. Try again."); }
    finally { setReplying(false); }
  };

  // ---- shared styles ----
  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "9px 12px", borderRadius: 8, border: "1px solid var(--ct-line)",
    background: "var(--ct-surface)", fontFamily: "inherit", fontSize: 13, color: "var(--ct-ink)", outline: "none", boxSizing: "border-box",
  };
  const labelStyle: React.CSSProperties = { display: "block", fontSize: 12.5, fontWeight: 600, color: "var(--ct-ink-2)", marginBottom: 6 };
  const isSuccess = result && result.startsWith("✓");

  const tabBtn = (active: boolean): React.CSSProperties => ({
    padding: "7px 16px", borderRadius: 8, border: `1px solid ${active ? "var(--ct-accent)" : "var(--ct-line)"}`,
    background: active ? "var(--ct-accent-bg)" : "transparent", color: active ? "var(--ct-accent)" : "var(--ct-ink-2)",
    fontFamily: "inherit", fontSize: 13, fontWeight: active ? 600 : 500, cursor: "pointer",
  });

  return (
    <div style={{ padding: "20px 24px 40px", maxWidth: 640 }}>
      <div style={{ fontSize: 15, fontWeight: 650, letterSpacing: "-0.01em", color: "var(--ct-ink)", marginBottom: 14 }}>
        Service Desk
      </div>

      {/* Mode switch */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <button style={tabBtn(mode === "new")} onClick={() => setMode("new")}>New ticket</button>
        <button style={tabBtn(mode === "list")} onClick={() => { setMode("list"); setOpenId(null); }}>Tickets</button>
      </div>

      {/* ============ NEW TICKET ============ */}
      {mode === "new" && (
        <div style={{ maxWidth: 520, border: "1px solid var(--ct-line)", borderRadius: 10, padding: 20, background: "var(--ct-surface)", display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={labelStyle}>Branch</label>
            {identity.branch ? (
              <div style={{ ...inputStyle, background: "var(--ct-surface-2)", fontWeight: 600 }}>{identity.branch}</div>
            ) : (
              <select value={branchPick} onChange={(e) => setBranchPick(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
                <option value="">Select a branch…</option>
                {ALL_LOCATIONS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            )}
          </div>

          {!identity.email && (
            <div>
              <label style={labelStyle}>Your email</label>
              <input type="email" value={manualEmail} onChange={(e) => setManualEmail(e.target.value)} placeholder="you@orderlords.com" style={inputStyle} />
            </div>
          )}

          <div>
            <label style={labelStyle}>Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
              <option value="">Select a category…</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Brief summary of the issue" style={inputStyle} />
          </div>

          <div>
            <label style={labelStyle}>Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Details, what happened, what you've tried…" rows={4} style={{ ...inputStyle, resize: "vertical" }} />
          </div>

          <div>
            <label style={labelStyle}>Attachment (optional)</label>
            <input type="file" accept="image/*,.pdf" onChange={(e) => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)} style={{ ...inputStyle, padding: "7px 10px", cursor: "pointer" }} />
            {file && <div style={{ fontSize: 11.5, color: "var(--ct-ink-3)", marginTop: 6 }}>{file.name} · {Math.round(file.size / 1024)} KB</div>}
          </div>

          <div>
            <label style={labelStyle}>Priority</label>
            <div style={{ display: "flex", gap: 6 }}>
              {PRIORITIES.map((p) => {
                const on = priority === p.value;
                return (
                  <button key={p.value} onClick={() => setPriority(p.value)}
                    style={{ flex: 1, padding: "7px 10px", borderRadius: 8,
                      border: `1px solid ${on ? "var(--ct-accent)" : "var(--ct-line)"}`,
                      background: on ? "var(--ct-accent-bg)" : "transparent",
                      color: on ? "var(--ct-accent)" : "var(--ct-ink-2)",
                      fontFamily: "inherit", fontSize: 12.5, fontWeight: on ? 600 : 500, cursor: "pointer" }}>
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {result && <div style={{ fontSize: 12.5, color: isSuccess ? "var(--ct-ok)" : "var(--ct-crit)", textAlign: "center", fontWeight: 600 }}>{result}</div>}

          <button onClick={handleSubmit} disabled={!canSubmit || submitting}
            style={{ padding: "10px 20px", borderRadius: 8, border: "none",
              background: canSubmit ? "var(--ct-accent)" : "var(--ct-surface-3, #e2e8f0)",
              color: canSubmit ? "#fff" : "var(--ct-ink-3)", fontFamily: "inherit", fontSize: 13, fontWeight: 600,
              cursor: canSubmit && !submitting ? "pointer" : "not-allowed", alignSelf: "flex-end" }}>
            {submitting ? "Submitting…" : "Submit ticket"}
          </button>
        </div>
      )}

      {/* ============ TICKETS LIST ============ */}
      {mode === "list" && openId === null && (
        <div style={{ border: "1px solid var(--ct-line)", borderRadius: 10, overflow: "hidden", background: "var(--ct-surface)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 16px", borderBottom: "1px solid var(--ct-line)" }}>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ct-ink)" }}>
              {identity.branch ? `${identity.branch} tickets` : "Recent tickets · all branches"}
            </span>
            <button onClick={loadList} style={{ ...tabBtn(false), padding: "5px 11px", fontSize: 12 }}>Refresh</button>
          </div>

          {listLoading && <div style={{ padding: "40px 16px", textAlign: "center", color: "var(--ct-ink-3)", fontSize: 13 }}>Loading…</div>}
          {listErr && <div style={{ padding: "40px 16px", textAlign: "center", color: "var(--ct-crit)", fontSize: 13 }}>{listErr}</div>}
          {!listLoading && !listErr && visibleTickets.length === 0 && <div style={{ padding: "40px 16px", textAlign: "center", color: "var(--ct-ink-3)", fontSize: 13 }}>No tickets yet.</div>}

          {!listLoading && visibleTickets.map((t, i) => (
            <div key={t.id} onClick={() => openTicket(t.id)}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 16px", cursor: "pointer",
                borderBottom: i === visibleTickets.length - 1 ? "none" : "1px solid var(--ct-line-soft)" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--ct-row-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
              <span className="ct-num" style={{ fontSize: 12, color: "var(--ct-ink-3)", minWidth: 34 }}>#{t.id}</span>
              <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: "var(--ct-ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.subject}</span>
              <span style={{ fontSize: 11.5, color: "var(--ct-ink-3)" }}>{PRIO_LABEL[t.priority] || ""}</span>
              <StatusPill status={t.status} />
            </div>
          ))}
        </div>
      )}

      {/* ============ TICKET DETAIL ============ */}
      {mode === "list" && openId !== null && (
        <div>
          <button onClick={() => { setOpenId(null); }} style={{ ...tabBtn(false), padding: "6px 12px", fontSize: 12, marginBottom: 14 }}>‹ Back to tickets</button>

          {detailLoading && <div style={{ padding: "40px 16px", textAlign: "center", color: "var(--ct-ink-3)", fontSize: 13 }}>Loading…</div>}

          {!detailLoading && detail && (
            <div style={{ border: "1px solid var(--ct-line)", borderRadius: 10, background: "var(--ct-surface)", overflow: "hidden" }}>
              <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--ct-line)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 14, fontWeight: 650, color: "var(--ct-ink)" }}>#{detail.ticket.id} · {detail.ticket.subject}</span>
                <StatusPill status={detail.ticket.status} />
              </div>

              <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ borderRadius: 8, padding: "10px 12px", background: "var(--ct-surface-2)" }}>
                  <div style={{ fontSize: 11.5, color: "var(--ct-ink-3)", marginBottom: 4 }}>Customer · {fmt(detail.ticket.createdAt)}</div>
                  <div style={{ fontSize: 13, color: "var(--ct-ink)", whiteSpace: "pre-wrap" }}>{detail.ticket.description || "(no message)"}</div>
                </div>

                {detail.conversations.filter((c) => !c.private).map((c) => (
                  <div key={c.id} style={{ borderRadius: 8, padding: "10px 12px",
                    background: c.incoming ? "var(--ct-surface-2)" : "var(--ct-accent-bg)",
                    alignSelf: c.incoming ? "flex-start" : "stretch" }}>
                    <div style={{ fontSize: 11.5, color: "var(--ct-ink-3)", marginBottom: 4 }}>
                      {c.incoming ? "Customer" : "Team"}{c.fromEmail ? ` · ${c.fromEmail}` : ""} · {fmt(c.createdAt)}
                    </div>
                    <div style={{ fontSize: 13, color: "var(--ct-ink)", whiteSpace: "pre-wrap" }}>{c.body}</div>
                  </div>
                ))}
              </div>

              <div style={{ padding: 16, borderTop: "1px solid var(--ct-line)" }}>
                <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Write a reply…" rows={3}
                  style={{ ...inputStyle, resize: "vertical", marginBottom: 10 }} />
                {replyMsg && <div style={{ fontSize: 12, color: replyMsg.startsWith("✓") ? "var(--ct-ok)" : "var(--ct-crit)", marginBottom: 10, fontWeight: 600 }}>{replyMsg}</div>}
                <button onClick={sendReply} disabled={!replyText.trim() || replying}
                  style={{ padding: "9px 18px", borderRadius: 8, border: "none",
                    background: replyText.trim() ? "var(--ct-accent)" : "var(--ct-surface-3, #e2e8f0)",
                    color: replyText.trim() ? "#fff" : "var(--ct-ink-3)", fontFamily: "inherit", fontSize: 13, fontWeight: 600,
                    cursor: replyText.trim() && !replying ? "pointer" : "not-allowed", float: "right" }}>
                  {replying ? "Sending…" : "Send reply"}
                </button>
                <div style={{ clear: "both" }} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
